import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X, Ticket, Calendar, Users, DollarSign, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatPrice } from '@/utils/formatters';

const AdminCouponsPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    description: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    min_purchase_amount: '',
    applicable_plans: [],
    enabled: true
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      toast.error('Acceso denegado');
      return;
    }
    fetchCoupons();
  }, [isAdmin]);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/coupons');
      setCoupons(data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error('Error al cargar cupones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code || !formData.discount_value || !formData.valid_from || !formData.valid_until) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    try {
      const payload = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        description: formData.description || null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_until: new Date(formData.valid_until).toISOString(),
        min_purchase_amount: formData.min_purchase_amount ? parseFloat(formData.min_purchase_amount) : null,
        applicable_plans: formData.applicable_plans.length > 0 ? formData.applicable_plans : null,
        enabled: formData.enabled
      };

      if (editingCoupon) {
        await axiosInstance.put(`/coupons/${editingCoupon.id}`, payload);
        toast.success('Cupón actualizado');
      } else {
        await axiosInstance.post('/coupons', payload);
        toast.success('Cupón creado');
      }

      setDialogOpen(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      console.error('Error saving coupon:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar cupón');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('¿Estás seguro de eliminar este cupón?')) return;

    try {
      await axiosInstance.delete(`/coupons/${couponId}`);
      toast.success('Cupón eliminado');
      fetchCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast.error('Error al eliminar cupón');
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value.toString(),
      description: coupon.description || '',
      max_uses: coupon.max_uses?.toString() || '',
      valid_from: new Date(coupon.valid_from).toISOString().slice(0, 16),
      valid_until: new Date(coupon.valid_until).toISOString().slice(0, 16),
      min_purchase_amount: coupon.min_purchase_amount?.toString() || '',
      applicable_plans: coupon.applicable_plans || [],
      enabled: coupon.enabled
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      description: '',
      max_uses: '',
      valid_from: '',
      valid_until: '',
      min_purchase_amount: '',
      applicable_plans: [],
      enabled: true
    });
  };

  const togglePlan = (planType) => {
    setFormData(prev => ({
      ...prev,
      applicable_plans: prev.applicable_plans.includes(planType)
        ? prev.applicable_plans.filter(p => p !== planType)
        : [...prev.applicable_plans, planType]
    }));
  };

  const getCouponStatus = (coupon) => {
    const now = new Date();
    const validFrom = new Date(coupon.valid_from);
    const validUntil = new Date(coupon.valid_until);

    if (!coupon.enabled) return { label: 'Deshabilitado', color: 'bg-slate-500' };
    if (now < validFrom) return { label: 'Próximo', color: 'bg-blue-500' };
    if (now > validUntil) return { label: 'Expirado', color: 'bg-red-500' };
    if (coupon.max_uses && coupon.times_used >= coupon.max_uses) return { label: 'Agotado', color: 'bg-amber-500' };
    return { label: 'Activo', color: 'bg-green-500' };
  };

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-2">
                Gestión de Cupones y Descuentos
              </h1>
              <p className="text-slate-600">Crea y administra códigos promocionales</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary-hover">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Cupón
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCoupon ? 'Editar Cupón' : 'Crear Nuevo Cupón'}</DialogTitle>
                  <DialogDescription>
                    Configura los detalles del cupón de descuento
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="code">Código del Cupón *</Label>
                      <Input
                        id="code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        placeholder="VERANO2025"
                        className="mt-1"
                        disabled={!!editingCoupon}
                      />
                    </div>
                    <div>
                      <Label htmlFor="discount_type">Tipo de Descuento *</Label>
                      <Select value={formData.discount_type} onValueChange={(value) => setFormData({ ...formData, discount_type: value })}>
                        <SelectTrigger id="discount_type" className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Porcentaje</SelectItem>
                          <SelectItem value="fixed">Valor Fijo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="discount_value">
                        Valor * ({formData.discount_type === 'percentage' ? '%' : 'COP'})
                      </Label>
                      <Input
                        id="discount_value"
                        type="number"
                        value={formData.discount_value}
                        onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                        placeholder={formData.discount_type === 'percentage' ? '20' : '10000'}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="max_uses">Usos Máximos</Label>
                      <Input
                        id="max_uses"
                        type="number"
                        value={formData.max_uses}
                        onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                        placeholder="100"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">Descripción</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descuento de verano para nuevos usuarios"
                      className="mt-1"
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="valid_from">Válido Desde *</Label>
                      <Input
                        id="valid_from"
                        type="datetime-local"
                        value={formData.valid_from}
                        onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="valid_until">Válido Hasta *</Label>
                      <Input
                        id="valid_until"
                        type="datetime-local"
                        value={formData.valid_until}
                        onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="min_purchase">Monto Mínimo de Compra (COP)</Label>
                    <Input
                      id="min_purchase"
                      type="number"
                      value={formData.min_purchase_amount}
                      onChange={(e) => setFormData({ ...formData, min_purchase_amount: e.target.value })}
                      placeholder="50000"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Planes Aplicables (dejar vacío para todos)</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['publish', 'feature_7', 'feature_30'].map(plan => (
                        <Badge
                          key={plan}
                          className={`cursor-pointer ${
                            formData.applicable_plans.includes(plan)
                              ? 'bg-primary text-white'
                              : 'bg-slate-200 text-slate-700'
                          }`}
                          onClick={() => togglePlan(plan)}
                        >
                          {plan === 'publish' ? 'Publicar' : plan === 'feature_7' ? 'Destacar 7d' : 'Destacar 30d'}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="enabled"
                      checked={formData.enabled}
                      onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="enabled" className="cursor-pointer">Cupón habilitado</Label>
                  </div>

                  <div className="flex space-x-2 pt-4">
                    <Button type="submit" className="flex-1 bg-primary hover:bg-primary-hover">
                      <Save className="w-4 h-4 mr-2" />
                      {editingCoupon ? 'Actualizar' : 'Crear'} Cupón
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setDialogOpen(false); resetForm(); }}
                      className="flex-1"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancelar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {coupons.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {coupons.map((coupon) => {
                const status = getCouponStatus(coupon);
                return (
                  <motion.div
                    key={coupon.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="relative">
                      <CardHeader>
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={status.color}>{status.label}</Badge>
                          <div className="flex space-x-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(coupon)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(coupon.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <CardTitle className="text-2xl font-heading font-bold text-primary flex items-center">
                          <Ticket className="w-5 h-5 mr-2" />
                          {coupon.code}
                        </CardTitle>
                        <CardDescription>{coupon.description || 'Sin descripción'}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Descuento:</span>
                            <span className="font-semibold text-slate-900">
                              {coupon.discount_type === 'percentage'
                                ? `${coupon.discount_value}%`
                                : formatPrice(coupon.discount_value)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600 flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              Válido hasta:
                            </span>
                            <span className="text-slate-900">
                              {new Date(coupon.valid_until).toLocaleDateString('es-CO')}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600 flex items-center">
                              <Users className="w-3 h-3 mr-1" />
                              Usado:
                            </span>
                            <span className="text-slate-900">
                              {coupon.times_used} {coupon.max_uses ? `/ ${coupon.max_uses}` : ''}
                            </span>
                          </div>
                          {coupon.min_purchase_amount && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-slate-600 flex items-center">
                                <DollarSign className="w-3 h-3 mr-1" />
                                Compra mínima:
                              </span>
                              <span className="text-slate-900">{formatPrice(coupon.min_purchase_amount)}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
              <Ticket className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-xl text-slate-600 mb-4">No hay cupones creados</p>
              <p className="text-slate-500 mb-6">Crea tu primer cupón para comenzar</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary-hover">
                <Plus className="w-4 h-4 mr-2" />
                Crear Cupón
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminCouponsPage;
